import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { Calendar, Car, Mail, Phone, UserRound, Users } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { api } from "@/lib/apiClient";
import {
  addDaysLocal,
  countNightsLocal,
  formatIsoDateShort,
  todayLocal,
} from "@/lib/bookingDates";
import { formatGuestName, resolveGuestName } from "@/lib/guestDisplay";
import {
  buildPitchTimeline,
  type PitchTimelineSegment,
  type PitchTimelineSegmentKind,
} from "@/lib/pitchPlanner";
import { pitchUsageTypeLabel } from "@/lib/pitchUsageType";
import { statusKindLabel, type SitePlanPitchStatus } from "@/lib/sitePlan";
import { useGuestById } from "@/lib/useGuestById";
import { formatLicensePlate, vehicleTypeLabel } from "@/lib/vehicle";
import type { Booking, Guest, Pitch } from "@/types/camping";

const DEFAULT_RANGE_DAYS = 60;

function segmentKindLabel(kind: PitchTimelineSegmentKind): string {
  switch (kind) {
    case "free":
      return "Frei";
    case "occupied":
      return "Belegt";
    case "blocked_pitch":
      return "Platz gesperrt";
  }
}

function segmentRangeLabel(from: string, to: string): string {
  const nights = countNightsLocal(from, to);
  const range = `${formatIsoDateShort(from)} – ${formatIsoDateShort(to)}`;
  if (nights <= 0) return range;
  return `${range} (${nights} ${nights === 1 ? "Nacht" : "Nächte"})`;
}

function partyLabel(booking: Booking): string | null {
  const adults = booking.adults ?? 0;
  const children = booking.children ?? 0;
  const dogs = booking.dogs ?? 0;
  const parts: string[] = [];
  if (adults > 0) parts.push(adults === 1 ? "1 Erwachsener" : `${adults} Erwachsene`);
  if (children === 1) parts.push("1 Kind");
  else if (children > 0) parts.push(`${children} Kinder`);
  if (dogs === 1) parts.push("1 Hund");
  else if (dogs > 0) parts.push(`${dogs} Hunde`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

function stayRangeLabel(booking: Booking): string {
  const nights = booking.nights || countNightsLocal(booking.arrival_date, booking.departure_date);
  const range = `${formatIsoDateShort(booking.arrival_date)} – ${formatIsoDateShort(booking.departure_date)}`;
  if (nights <= 0) return range;
  return `${range} · ${nights} ${nights === 1 ? "Nacht" : "Nächte"}`;
}

function presenceTitle(booking: Booking | null, referenceDate: string): string {
  if (!booking) return "Anwesend";
  if (booking.status === "checked_in" && booking.departure_date < referenceDate) {
    return "Noch vor Ort";
  }
  if (booking.status === "checked_in") return "Anwesend";
  if (booking.status === "requested") return "Angefragt";
  if (booking.arrival_date > referenceDate) return "Reserviert";
  return "Belegt";
}

function presenceNotice(booking: Booking | null, referenceDate: string): string | null {
  if (!booking || booking.status !== "checked_in") return null;
  if (booking.departure_date < referenceDate) {
    return `Geplante Abreise war am ${formatIsoDateShort(booking.departure_date)}. Der Gast ist noch nicht ausgecheckt und steht deshalb nicht in der Planung ab heute.`;
  }
  if (booking.departure_date === referenceDate) {
    return "Abreise heute. Der Gast ist noch eingecheckt. Der Abreisetag kann für eine neue Reservierung genutzt werden.";
  }
  return null;
}

function guestPhone(guest: Guest | null): string | null {
  const phone = guest?.phone_mobile?.trim() || guest?.phone?.trim();
  return phone || null;
}

function MetaChip({ children }: { children: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-fill-tertiary px-2.5 py-1 text-xs font-medium text-label-secondary">
      {children}
    </span>
  );
}

function Fact({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 gap-2.5">
      <span className="mt-0.5 shrink-0 text-label-tertiary">{icon}</span>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-label-tertiary">{label}</p>
        <div className="mt-0.5 text-sm text-label">{children}</div>
      </div>
    </div>
  );
}

function TimelineSegmentButton({
  segment,
  guestName,
  canEdit,
  caution,
  onSelectFree,
  onSelectOccupied,
}: {
  segment: PitchTimelineSegment;
  guestName: (guestId: string, booking?: Booking | null) => string;
  canEdit: boolean;
  caution?: string | null;
  onSelectFree: (from: string, to: string) => void;
  onSelectOccupied: (booking: Booking) => void;
}) {
  const label = segmentKindLabel(segment.kind);
  const range = segmentRangeLabel(segment.from, segment.to);
  const booking = segment.booking;
  const clickable =
    canEdit &&
    (segment.kind === "free" || (segment.kind === "occupied" && !!booking));

  const handleClick = () => {
    if (segment.kind === "free") {
      onSelectFree(segment.from, segment.to);
      return;
    }
    if (segment.kind === "occupied" && booking) {
      onSelectOccupied(booking);
    }
  };

  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={handleClick}
      className={clsx(
        "flex w-full flex-col gap-1 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-1",
        segment.kind === "free" &&
          "border-status-green/35 bg-status-green/8 hover:border-status-green/50 hover:bg-status-green/12",
        segment.kind === "occupied" &&
          "border-accent/30 bg-accent-soft/40 hover:border-accent/50 hover:bg-accent-soft/70",
        segment.kind === "blocked_pitch" &&
          "border-separator bg-fill-tertiary/40",
        !clickable && "cursor-default opacity-90",
        clickable && "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
      )}
    >
      <span
        className={clsx(
          "shrink-0 font-semibold",
          segment.kind === "free" && "text-status-green",
          segment.kind === "occupied" && "text-accent",
          segment.kind === "blocked_pitch" && "text-label-secondary",
        )}
      >
        {label}
      </span>
      <span className="tabular-nums font-medium text-label">{range}</span>
      {booking ? (
        <span className="flex min-w-0 flex-wrap items-center gap-2 text-label-secondary">
          <span className="font-medium text-label">{guestName(booking.guest_id, booking)}</span>
          <span className="font-mono text-xs text-label-tertiary">{booking.booking_number}</span>
          <StatusBadge status={booking.status} />
        </span>
      ) : segment.kind === "blocked_pitch" ? (
        <span className="text-label-secondary">Stellplatz ist inaktiv oder manuell gesperrt.</span>
      ) : null}
      {clickable && segment.kind === "occupied" ? (
        <span className="text-xs font-medium text-accent sm:ml-auto">Aufenthalt öffnen</span>
      ) : null}
      {clickable && segment.kind === "free" ? (
        <span className="text-xs font-medium text-status-green sm:ml-auto">Neue Reservierung</span>
      ) : null}
      {caution ? (
        <span className="basis-full text-xs text-status-orange">{caution}</span>
      ) : null}
    </button>
  );
}

export interface PitchPlanningModalProps {
  open: boolean;
  pitchStatus: SitePlanPitchStatus | null;
  pitch: Pitch | null;
  bookings: Booking[];
  bookingsLoading?: boolean;
  referenceDate: string;
  canEdit: boolean;
  onClose: () => void;
  onSelectFree: (pitchId: string, from: string, to: string) => void;
  onSelectOccupied: (booking: Booking) => void;
}

export function PitchPlanningModal({
  open,
  pitchStatus,
  pitch,
  bookings,
  bookingsLoading = false,
  referenceDate,
  canEdit,
  onClose,
  onSelectFree,
  onSelectOccupied,
}: PitchPlanningModalProps) {
  const navigate = useNavigate();
  const contentSnapshot = useRef<{
    pitch: Pitch | null;
    pitchStatus: SitePlanPitchStatus | null;
  }>({ pitch: null, pitchStatus: null });

  if (open && pitch) {
    contentSnapshot.current = { pitch, pitchStatus };
  }

  const displayPitch = open ? pitch : contentSnapshot.current.pitch;
  const displayPitchStatus = open ? pitchStatus : contentSnapshot.current.pitchStatus;
  const bookingId = displayPitchStatus?.booking_id ?? null;

  const listedBooking = useMemo(
    () => (bookingId ? (bookings.find((booking) => booking.id === bookingId) ?? null) : null),
    [bookings, bookingId],
  );

  const [fetchedBooking, setFetchedBooking] = useState<Booking | null>(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);

  useEffect(() => {
    if (!bookingId || listedBooking) {
      setBookingLoading(false);
      setBookingError(null);
      return;
    }
    let cancelled = false;
    setFetchedBooking(null);
    setBookingLoading(true);
    setBookingError(null);
    void api
      .get<Booking>(`/api/v1/bookings/${bookingId}`)
      .then((booking) => {
        if (!cancelled) setFetchedBooking(booking);
      })
      .catch(() => {
        if (!cancelled) {
          setFetchedBooking(null);
          setBookingError("Der Aufenthalt konnte nicht geladen werden.");
        }
      })
      .finally(() => {
        if (!cancelled) setBookingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bookingId, listedBooking]);

  const currentBooking =
    listedBooking ?? (fetchedBooking?.id === bookingId ? fetchedBooking : null);
  const guest = useGuestById(currentBooking?.guest_id);

  const today = todayLocal();
  const rangeFrom = useMemo(() => {
    if (!referenceDate) return today;
    return referenceDate < today ? referenceDate : today;
  }, [referenceDate, today]);

  const rangeToExclusive = addDaysLocal(rangeFrom, DEFAULT_RANGE_DAYS);

  const timelineBookings = useMemo(() => {
    if (!currentBooking) return bookings;
    if (bookings.some((booking) => booking.id === currentBooking.id)) return bookings;
    return [...bookings, currentBooking];
  }, [bookings, currentBooking]);

  const segments = useMemo(() => {
    if (!displayPitch) return [];
    return buildPitchTimeline(displayPitch, timelineBookings, rangeFrom, rangeToExclusive, today);
  }, [displayPitch, timelineBookings, rangeFrom, rangeToExclusive, today]);

  const guestName = (id: string, booking?: Booking | null) => resolveGuestName(id, { booking });

  const displayName = guest
    ? formatGuestName(guest)
    : currentBooking?.guest_name?.trim() ||
      displayPitchStatus?.guest_name?.trim() ||
      null;

  const currentStatus = displayPitchStatus
    ? statusKindLabel(displayPitchStatus.status_kind, displayPitchStatus.transition_hint)
    : null;

  const pitchMeta = useMemo(() => {
    if (!displayPitch) return [];
    const chips: string[] = [];
    if (displayPitch.category_name) chips.push(displayPitch.category_name);
    if (displayPitch.usage_type && displayPitch.usage_type !== "tourism") {
      chips.push(pitchUsageTypeLabel(displayPitch.usage_type));
    }
    if (displayPitch.size_sqm) chips.push(`${displayPitch.size_sqm} m²`);
    if (displayPitch.has_electricity) chips.push("Strom");
    if (displayPitch.has_water) chips.push("Wasser");
    if (displayPitch.has_sewage) chips.push("Abwasser");
    return chips;
  }, [displayPitch]);

  const subtitle = [displayPitch?.label && displayPitch.label !== displayPitch.code ? displayPitch.label : null]
    .filter(Boolean)
    .join(" · ");

  const occupied = displayPitchStatus?.status_kind === "occupied";
  const blocked =
    displayPitchStatus?.status_kind === "blocked" || displayPitchStatus?.status_kind === "inactive";
  const notice = presenceNotice(currentBooking, referenceDate);
  const overstay =
    currentBooking?.status === "checked_in" &&
    currentBooking.departure_date <= referenceDate;
  const phone = guestPhone(guest);
  const email = guest?.email?.trim() || null;
  const people = currentBooking ? partyLabel(currentBooking) : null;
  const vehicle = currentBooking?.vehicle_type
    ? vehicleTypeLabel(currentBooking.vehicle_type)
    : null;
  const plate = currentBooking
    ? formatLicensePlate(currentBooking.license_plate, currentBooking.license_plate_country)
    : "—";
  const showPlate = plate !== "—";

  const freeSegmentForNewBooking =
    segments.find(
      (segment) =>
        segment.kind === "free" &&
        segment.from <= referenceDate &&
        segment.to > referenceDate,
    ) ?? segments.find((segment) => segment.kind === "free") ?? null;

  const openGuest = (guestId: string) => {
    onClose();
    navigate(`/guests/${guestId}`);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={displayPitch ? `Platz ${displayPitch.code}` : "Platzplanung"}
      description={subtitle || undefined}
      size="lg"
      instant
      footer={
        <div className="flex justify-end">
          <Button type="button" variant="secondary" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-1.5">
          {currentStatus ? (
            <span
              className={clsx(
                "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold",
                occupied && "bg-accent-soft text-accent",
                displayPitchStatus?.status_kind === "free" && "bg-status-green/15 text-status-green",
                blocked && "bg-fill-tertiary text-label-secondary",
              )}
            >
              {currentStatus}
            </span>
          ) : null}
          {pitchMeta.map((chip) => (
            <MetaChip key={chip}>{chip}</MetaChip>
          ))}
        </div>

        {occupied ? (
          <section className="overflow-hidden rounded-2xl border border-separator bg-card shadow-sm">
            <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-accent">
                    {presenceTitle(currentBooking, referenceDate)}
                  </p>
                  {currentBooking ? <StatusBadge status={currentBooking.status} /> : null}
                  {!currentBooking && displayPitchStatus?.booking_status ? (
                    <StatusBadge status={displayPitchStatus.booking_status} />
                  ) : null}
                </div>
                <div>
                  <h3 className="text-xl font-semibold tracking-tight text-label">
                    {displayName ?? (bookingLoading ? "Gast wird geladen…" : "Gast")}
                  </h3>
                  {displayPitchStatus?.booking_number ? (
                    <p className="mt-1 font-mono text-sm text-label-secondary">
                      {displayPitchStatus.booking_number}
                    </p>
                  ) : null}
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  {currentBooking ? (
                    <Fact icon={<Calendar className="h-4 w-4" aria-hidden />} label="Aufenthalt">
                      {stayRangeLabel(currentBooking)}
                    </Fact>
                  ) : (
                    <Fact icon={<Calendar className="h-4 w-4" aria-hidden />} label="Stichtag">
                      {formatIsoDateShort(referenceDate)}
                    </Fact>
                  )}
                  {people ? (
                    <Fact icon={<Users className="h-4 w-4" aria-hidden />} label="Personen">
                      {people}
                    </Fact>
                  ) : null}
                  {vehicle || showPlate ? (
                    <Fact icon={<Car className="h-4 w-4" aria-hidden />} label="Fahrzeug">
                      {[vehicle && vehicle !== "—" ? vehicle : null, showPlate ? plate : null]
                        .filter(Boolean)
                        .join(" · ")}
                    </Fact>
                  ) : null}
                  {phone ? (
                    <Fact icon={<Phone className="h-4 w-4" aria-hidden />} label="Telefon">
                      <a className="text-accent hover:underline" href={`tel:${phone}`}>
                        {phone}
                      </a>
                    </Fact>
                  ) : null}
                  {email ? (
                    <Fact icon={<Mail className="h-4 w-4" aria-hidden />} label="E-Mail">
                      <a className="break-all text-accent hover:underline" href={`mailto:${email}`}>
                        {email}
                      </a>
                    </Fact>
                  ) : null}
                </div>
                {bookingError ? <p className="text-sm text-status-red">{bookingError}</p> : null}
              </div>

              <div className="flex shrink-0 flex-col gap-2 sm:w-52">
                <Button
                  type="button"
                  disabled={!currentBooking}
                  loading={bookingLoading && !currentBooking}
                  leftIcon={<UserRound className="h-4 w-4" aria-hidden />}
                  onClick={() => {
                    if (currentBooking) onSelectOccupied(currentBooking);
                  }}
                >
                  Aufenthalt öffnen
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!currentBooking?.guest_id}
                  onClick={() => {
                    if (currentBooking?.guest_id) openGuest(currentBooking.guest_id);
                  }}
                >
                  Zum Gast
                </Button>
              </div>
            </div>
            {notice ? (
              <p className="border-t border-separator bg-status-orange/10 px-4 py-3 text-sm text-status-orange">
                {notice}
              </p>
            ) : null}
          </section>
        ) : displayPitchStatus?.status_kind === "free" ? (
          <section className="flex flex-col gap-3 rounded-2xl border border-status-green/30 bg-status-green/8 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-status-green">Frei</p>
              <p className="mt-1 text-sm text-label">
                Am {formatIsoDateShort(referenceDate)} ist der Platz frei.
              </p>
            </div>
            {canEdit && freeSegmentForNewBooking && displayPitch ? (
              <Button
                type="button"
                onClick={() =>
                  onSelectFree(
                    displayPitch.id,
                    freeSegmentForNewBooking.from,
                    freeSegmentForNewBooking.to,
                  )
                }
              >
                Neue Reservierung
              </Button>
            ) : null}
          </section>
        ) : blocked ? (
          <section className="rounded-2xl border border-separator bg-fill-tertiary/40 p-4">
            <p className="text-sm font-medium text-label">{currentStatus}</p>
            <ul className="mt-2 space-y-1 text-sm text-label-secondary">
              {(displayPitchStatus?.reasons.length
                ? displayPitchStatus.reasons
                : ["Dieser Platz kann derzeit nicht belegt werden."]
              ).map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="space-y-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
              Planung
            </h3>
            <p className="text-xs tabular-nums text-label-tertiary">
              {formatIsoDateShort(rangeFrom)} –{" "}
              {formatIsoDateShort(addDaysLocal(rangeToExclusive, -1))}
            </p>
          </div>
          <p className="text-xs text-label-tertiary">
            Freie Zeiträume starten eine neue Reservierung, belegte öffnen den Aufenthalt. Der
            Abreisetag zählt als frei.
          </p>

          {bookingsLoading && segments.every((segment) => segment.kind !== "occupied") ? (
            <p className="text-sm text-label-secondary">Belegungsdaten werden geladen…</p>
          ) : segments.length === 0 ? (
            <p className="text-sm text-label-secondary">Keine Planungsdaten für diesen Platz.</p>
          ) : (
            <ul className="max-h-[min(40vh,22rem)] space-y-2 overflow-y-auto pr-1">
              {segments.map((segment, idx) => (
                <li key={`${segment.kind}-${segment.from}-${segment.to}-${idx}`}>
                  <TimelineSegmentButton
                    segment={segment}
                    guestName={guestName}
                    canEdit={canEdit || segment.kind === "occupied"}
                    caution={
                      overstay &&
                      segment.kind === "free" &&
                      segment.from <= referenceDate &&
                      segment.to > referenceDate &&
                      displayName
                        ? `${displayName} ist noch nicht ausgecheckt.`
                        : null
                    }
                    onSelectFree={(from, to) => {
                      if (displayPitch) onSelectFree(displayPitch.id, from, to);
                    }}
                    onSelectOccupied={onSelectOccupied}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Modal>
  );
}
